
import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Image as ImageIcon, Film } from 'lucide-react';

interface MediaUploaderProps {
  onUpload: (urls: string[]) => void;
  onRemove?: (index: number) => void;
  maxFiles?: number;
  allowVideo?: boolean;
  maxDuration?: number;
  showPreview?: boolean;
  existingMedia?: string[];
  onRemoveExisting?: (index: number) => void;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  onUpload,
  onRemove,
  maxFiles = 5,
  allowVideo = true,
  maxDuration,
  showPreview = true,
  existingMedia = [],
  onRemoveExisting,
}) => {
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const filesArray = Array.from(e.target.files);
    const remainingSlots = maxFiles - (existingMedia.length + previewUrls.length);
    
    if (remainingSlots <= 0) {
      alert(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }
    
    const selectedFiles = filesArray.slice(0, remainingSlots);
    
    // Create preview URLs
    const newPreviewUrls = selectedFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newPreviewUrls]);
    
    // Mock upload - In a real app, this would actually upload the files to a server
    // But for the demo, we'll just use the object URLs
    onUpload(newPreviewUrls);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleRemovePreview = (index: number) => {
    const newPreviewUrls = [...previewUrls];
    
    // Revoke the object URL to free memory
    URL.revokeObjectURL(newPreviewUrls[index]);
    
    newPreviewUrls.splice(index, 1);
    setPreviewUrls(newPreviewUrls);
    
    // Call onRemove if provided
    if (onRemove) {
      onRemove(index);
    }
  };
  
  const handleRemoveExisting = (index: number) => {
    if (onRemoveExisting) {
      onRemoveExisting(index);
    }
  };
  
  const handleCaptureMedia = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const totalMedia = existingMedia.length + previewUrls.length;
  const canAddMore = totalMedia < maxFiles;
  const type = allowVideo ? 'media' : 'image';
  
  return (
    <div className="space-y-3">
      {showPreview && (
        <div className="flex flex-wrap gap-3">
          {/* Existing Media */}
          {existingMedia.map((url, index) => (
            <div key={`existing-${index}`} className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100">
              {!allowVideo || !url.includes('.mp4') ? (
                <img src={url} alt={`Uploaded ${index}`} className="w-full h-full object-cover" />
              ) : (
                <video src={url} className="w-full h-full object-cover" controls />
              )}
              {onRemoveExisting && (
                <button
                  type="button"
                  onClick={() => handleRemoveExisting(index)}
                  className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          
          {/* Preview Media */}
          {previewUrls.map((url, index) => (
            <div key={`preview-${index}`} className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100">
              {!allowVideo || !url.includes('.mp4') ? (
                <img src={url} alt={`Preview ${index}`} className="w-full h-full object-cover" />
              ) : (
                <video src={url} className="w-full h-full object-cover" controls />
              )}
              <button
                type="button"
                onClick={() => handleRemovePreview(index)}
                className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          
          {/* Add Media Button */}
          {canAddMore && (
            <button
              type="button"
              onClick={handleCaptureMedia}
              className="w-24 h-24 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-lime transition-colors"
            >
              {allowVideo ? (
                <>
                  <Camera size={24} className="mb-1" />
                  <span className="text-xs">Add Media</span>
                </>
              ) : (
                <>
                  <ImageIcon size={24} className="mb-1" />
                  <span className="text-xs">Add Image</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
      
      <div className="flex space-x-3">
        <button
          type="button"
          onClick={handleCaptureMedia}
          disabled={!canAddMore}
          className={`flex items-center justify-center px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 
                    ${canAddMore ? 'hover:bg-gray-200 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
        >
          <Camera size={20} className="mr-2" />
          <span>{allowVideo ? 'Capture Media' : 'Take Photo'}</span>
        </button>
        
        <button
          type="button"
          onClick={handleCaptureMedia}
          disabled={!canAddMore}
          className={`flex items-center justify-center px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 
                    ${canAddMore ? 'hover:bg-gray-200 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
        >
          <Upload size={20} className="mr-2" />
          <span>Upload</span>
        </button>
      </div>
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={allowVideo ? 'image/*,video/*' : 'image/*'}
        capture={allowVideo ? undefined : 'environment'}
        className="hidden"
        multiple={maxFiles > 1}
      />
      
      <div className="text-xs text-gray-500">
        {totalMedia} of {maxFiles} {allowVideo ? 'media files' : 'images'}
        {maxDuration && allowVideo ? ` (max ${maxDuration}s per video)` : ''}
      </div>
    </div>
  );
};

export default MediaUploader;
